/**
 * DENEB London Theme - Shared Utilities
 * Global utility functions for theme functionality
 */

// ============================================
// Money Formatting
// ============================================

/**
 * Format cents to currency string using Intl.NumberFormat.
 * Reads currency and locale from window.theme (set inline in layout/theme.liquid
 * before this script) so output is always store-currency-aware.
 * @param {number} cents - Amount in cents
 * @returns {string} Formatted money string (e.g. "£1,234.00")
 */
function formatMoney(cents) {
  var currency = (window.theme && window.theme.currency) || 'GBP';
  var locale = (window.theme && window.theme.locale) || 'en';
  return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(cents / 100);
}
window.theme = window.theme || {};
window.theme.formatMoney = formatMoney;

// ============================================
// Product Card Swatches (shared)
// ============================================

/**
 * Update a single product card's images, link(s) and price from a swatch's
 * data attributes. This is the behaviour shared by the collection grid and the
 * search results grid; selector differences between the two layouts are passed
 * in via `options`.
 *
 * Reads from the swatch element's dataset:
 *   data-variant-image, data-variant-hover-image, data-variant-url,
 *   data-variant-price, data-variant-compare-price
 *
 * @param {HTMLElement} card - The product card container element.
 * @param {HTMLElement} swatchesContainer - The `.product-card__swatches` element.
 * @param {HTMLElement} swatch - The clicked/selected `.product-card__swatch`.
 * @param {Object} options - Layout-specific selectors (all relative to `card`).
 * @param {string} [options.primaryImageSelector] - Selector for the primary <img>.
 * @param {string} [options.hoverImageSelector] - Selector for the hover <img> (collection only).
 * @param {string} [options.hoverImageLinkSelector] - Selector for the element that carries `has-hover-image`.
 * @param {string[]} [options.linkSelectors] - Selectors for product link(s) whose href updates.
 * @param {string} [options.priceSelector] - Selector for the price container.
 */
function updateProductCardFromSwatch(card, swatchesContainer, swatch, options) {
  if (!card || !swatchesContainer || !swatch) return;
  options = options || {};

  var variantImage = swatch.dataset.variantImage;
  var variantHoverImage = swatch.dataset.variantHoverImage;
  var variantUrl = swatch.dataset.variantUrl;
  var variantPrice = swatch.dataset.variantPrice;
  var variantComparePrice = swatch.dataset.variantComparePrice;

  // Update active swatch highlight
  swatchesContainer.querySelectorAll('.product-card__swatch').forEach(function (s) {
    s.classList.remove('is-active');
  });
  swatch.classList.add('is-active');

  // Primary image
  var primaryImage = options.primaryImageSelector ? card.querySelector(options.primaryImageSelector) : null;
  if (variantImage && primaryImage) {
    primaryImage.src = variantImage;
    primaryImage.removeAttribute('srcset');
  }

  // Hover image + has-hover-image class (collection only; search passes no selectors)
  if (options.hoverImageSelector) {
    var hoverImage = card.querySelector(options.hoverImageSelector);
    var hoverImageLink = options.hoverImageLinkSelector ? card.querySelector(options.hoverImageLinkSelector) : null;
    if (variantHoverImage && hoverImage) {
      hoverImage.src = variantHoverImage;
      hoverImage.removeAttribute('srcset');
      if (hoverImageLink) hoverImageLink.classList.add('has-hover-image');
    } else if (hoverImageLink) {
      hoverImageLink.classList.remove('has-hover-image');
    }
  }

  // Product link(s)
  if (variantUrl && options.linkSelectors) {
    options.linkSelectors.forEach(function (selector) {
      var link = card.querySelector(selector);
      if (link) link.href = variantUrl;
    });
  }

  // Price display
  var priceContainer = options.priceSelector ? card.querySelector(options.priceSelector) : null;
  if (priceContainer && variantPrice) {
    var priceCents = parseInt(variantPrice, 10);
    var compareCents = variantComparePrice ? parseInt(variantComparePrice, 10) : 0;
    var currentPriceEl = priceContainer.querySelector('.price__current');
    var comparePriceEl = priceContainer.querySelector('.price__compare');

    if (currentPriceEl) {
      currentPriceEl.textContent = formatMoney(priceCents);
    }

    if (compareCents > priceCents) {
      priceContainer.classList.add('price--on-sale');
      if (comparePriceEl) {
        comparePriceEl.textContent = formatMoney(compareCents);
      } else if (currentPriceEl) {
        var newCompare = document.createElement('span');
        newCompare.className = 'price__compare';
        newCompare.textContent = formatMoney(compareCents);
        priceContainer.insertBefore(newCompare, currentPriceEl);
      }
    } else {
      priceContainer.classList.remove('price--on-sale');
      if (comparePriceEl) {
        comparePriceEl.remove();
      }
    }
  }
}

/**
 * Bind swatch click handlers to every `.product-card__swatches` container found
 * under `root`. On click, the matching card's images/links/price are updated via
 * updateProductCardFromSwatch().
 *
 * @param {ParentNode} [root=document] - Subtree to scan for swatch containers.
 * @param {Object} options - Layout-specific config.
 * @param {string} options.cardSelector - Selector (passed to closest()) for the card container.
 * @param {string} [options.primaryImageSelector] - See updateProductCardFromSwatch.
 * @param {string} [options.hoverImageSelector] - See updateProductCardFromSwatch.
 * @param {string} [options.hoverImageLinkSelector] - See updateProductCardFromSwatch.
 * @param {string[]} [options.linkSelectors] - See updateProductCardFromSwatch.
 * @param {string} [options.priceSelector] - See updateProductCardFromSwatch.
 * @param {boolean} [options.guardInitialized] - Skip containers already flagged
 *   `data-initialized`, and flag the ones bound (used by the collection grid to
 *   avoid double-binding across Load More / editor reloads).
 */
function initProductCardSwatches(root, options) {
  root = root || document;
  options = options || {};
  var cardSelector = options.cardSelector || '.product-card';

  root.querySelectorAll('.product-card__swatches').forEach(function (swatchesContainer) {
    if (options.guardInitialized && swatchesContainer.dataset.initialized) return;
    if (options.guardInitialized) swatchesContainer.dataset.initialized = 'true';

    var card = swatchesContainer.closest(cardSelector);
    if (!card) return;

    swatchesContainer.querySelectorAll('.product-card__swatch').forEach(function (swatch) {
      swatch.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        updateProductCardFromSwatch(card, swatchesContainer, this, options);
      });
    });
  });
}

window.theme.updateProductCardFromSwatch = updateProductCardFromSwatch;
window.theme.initProductCardSwatches = initProductCardSwatches;

